/**
 * Build Divergence Guard — Detection + Event Dispatch ONLY.
 * 
 * ARCHITECTURAL RULE: This module NEVER reloads the page or navigates.
 * It detects build divergence, logs it, and dispatches a custom event
 * so React Query can invalidate caches. The user must manually reload.
 */

import { debugLog, debugError } from './debugLogger';

const BUILD_ID_KEY = 'netuno-build-id';
const REMOTE_CHECK_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes
const VISIBILITY_CHECK_COOLDOWN_MS = 30 * 1000; // 30s after tab return

let remoteCheckTimer: number | null = null;
let localCheckTimer: number | null = null;
let lastRemoteCheck = 0;

// Declared via Vite `define` — replaced at build time as a bare identifier
declare const __APP_BUILD_ID__: string;

function getCurrentBuildId(): string {
  try {
    return typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Handle build divergence — log + dispatch event ONLY.
 * NO reload, NO cache purge, NO navigation.
 */
function handleBuildDivergence(reason: string, oldBuild: string, newBuild: string) {
  debugLog('build', `DIVERGÊNCIA DETECTADA — ${reason}`, { oldBuild, newBuild });
  console.warn(`[BuildGuard] Build divergence: ${oldBuild} → ${newBuild} (${reason}) — reload manual recomendado`);

  // Notify React Query to invalidate caches
  window.dispatchEvent(new CustomEvent('netuno:build-changed', { detail: { oldBuild, newBuild } }));

  // Store NEW build id so we don't keep alerting
  try {
    localStorage.setItem(BUILD_ID_KEY, newBuild);
  } catch { /* storage unavailable */ }
}

// ── Remote build check via /build-id.json ────────────────────

async function fetchRemoteBuildId(): Promise<string | null> {
  try {
    const resp = await fetch('/build-id.json', {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate', 'Pragma': 'no-cache' },
    });

    if (!resp.ok) {
      debugError('build', `ERRO: /build-id.json retornou status ${resp.status}`);
      return null;
    }

    const data = await resp.json();
    if (!data || typeof data.buildId !== 'string' || data.buildId.length === 0) {
      debugError('build', '/build-id.json sem buildId válido', { data });
      return null;
    }

    return data.buildId;
  } catch (err) {
    debugError('build', 'Falha ao buscar /build-id.json', { error: String(err) });
    return null;
  }
}

async function checkRemoteBuild() {
  const now = Date.now();
  if (now - lastRemoteCheck < VISIBILITY_CHECK_COOLDOWN_MS) return;
  lastRemoteCheck = now;

  const currentBuild = getCurrentBuildId();
  if (currentBuild === 'unknown') return;

  const remoteBuild = await fetchRemoteBuildId();
  debugLog('build', 'Remote build check', { current: currentBuild, remote: remoteBuild });

  if (remoteBuild && remoteBuild !== currentBuild) {
    handleBuildDivergence('remote /build-id.json divergence', currentBuild, remoteBuild);
  }
}

// ── Local build check ────────────────────

function checkLocalBuild(): boolean {
  const currentBuild = getCurrentBuildId();
  if (currentBuild === 'unknown') return false;

  const storedBuild = localStorage.getItem(BUILD_ID_KEY);

  if (!storedBuild) {
    try { localStorage.setItem(BUILD_ID_KEY, currentBuild); } catch {}
    debugLog('build', 'Primeiro acesso — build registrado', { build: currentBuild });
    return false;
  }

  if (storedBuild !== currentBuild) {
    handleBuildDivergence('local build divergence', storedBuild, currentBuild);
    return true;
  }

  return false;
}

// ── Install ────────────────────

export function installBuildGuard() {
  if (typeof window === 'undefined') return () => {};

  // 1. Immediate local check
  checkLocalBuild();

  // 2. Periodic remote check (every 2 min)
  remoteCheckTimer = window.setInterval(() => {
    if (document.visibilityState === 'visible' && navigator.onLine) {
      void checkRemoteBuild();
    }
  }, REMOTE_CHECK_INTERVAL_MS);

  // 3. Periodic local check (every 2 min — supports multi-tab detection)
  localCheckTimer = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      checkLocalBuild();
    }
  }, REMOTE_CHECK_INTERVAL_MS);

  // 4. Check on visibility return
  const handleVisibility = () => {
    if (document.visibilityState === 'visible') {
      checkLocalBuild();
      void checkRemoteBuild();
    }
  };

  // 5. Check on focus return
  const handleFocus = () => {
    checkLocalBuild();
    void checkRemoteBuild();
  };

  // 6. Check on reconnect
  const handleOnline = () => {
    void checkRemoteBuild();
  };

  document.addEventListener('visibilitychange', handleVisibility);
  window.addEventListener('focus', handleFocus);
  window.addEventListener('online', handleOnline);

  debugLog('build', 'BuildGuard instalado (log-only, sem reload)', { buildId: getCurrentBuildId() });

  return () => {
    if (remoteCheckTimer) { clearInterval(remoteCheckTimer); remoteCheckTimer = null; }
    if (localCheckTimer) { clearInterval(localCheckTimer); localCheckTimer = null; }
    document.removeEventListener('visibilitychange', handleVisibility);
    window.removeEventListener('focus', handleFocus);
    window.removeEventListener('online', handleOnline);
  };
}
